/**
 * Thin, null-safe wrappers around the global `window.AmexSession` provided by the
 * one-identity-session shared script. Centralizing access here means callers no
 * longer repeat `window.AmexSession?.…` guards, and the "is the script loaded"
 * and "is the user authenticated" checks have a single definition.
 *
 * Every accessor treats a missing / not-yet-loaded AmexSession as a no-op:
 *  - queries return false
 *  - commands do nothing
 */

const getSession = () => (typeof window !== 'undefined' ? window.AmexSession : undefined);

// The shared script has loaded and AmexSession is available to call.
export const isAmexSessionReady = () => Boolean(getSession());

/**
 * Whether the user currently has an authenticated AmexSession. A missing session,
 * or an isAuthenticated() that throws, is treated as "not authenticated" — this is
 * what keeps auto-logout from firing during the unauthenticated sign-up journey.
 */
export const isAuthenticated = () => {
  try {
    return Boolean(getSession()?.isAuthenticated?.());
  } catch {
    return false;
  }
};

// Close the AmexSession iframe/session frame if one is open.
export const closeSessionFrame = () => {
  getSession()?.closeSessionFrame?.();
};

// Configure AmexSession (e.g. logout target + locale) before login.
export const setOptions = (options) => {
  getSession()?.options?.(options);
};

// Prompt the user to log in via AmexSession.
export const login = (config) => {
  getSession()?.login?.(config);
};

// Launch an enterprise experience (e.g. Smart Sign-Up) via AmexSession.
export const loadExperience = (config) => {
  getSession()?.loadExperience?.(config);
};
